import React, { useRef, useState, useMemo } from 'react';
import { Canvas, useFrame } from '@react-three/fiber';
import { Stars, OrbitControls, Float, Sparkles, PerspectiveCamera, Image } from '@react-three/drei';
import * as THREE from 'three';

// Import product images
import img1 from '../assets/i2r-01.jpg';
import img2 from '../assets/i2r-02.jpg';
import img3 from '../assets/i2r-03.jpg';
import img4 from '../assets/i2r-04.jpg';
import img5 from '../assets/i2r-05.png';
import img6 from '../assets/iot_plc_controller.png';

const productImages = [img1, img2, img3, img4, img5, img6];

const FloatingProduct = ({ img, position, delay }: { img: string, position: [number, number, number], delay: number }) => {
    const ref = useRef<THREE.Mesh>(null);
    const [start] = useState(Math.random() * 100);

    useFrame((state) => {
        if (ref.current) {
            const t = state.clock.elapsedTime + start + delay;
            // Opacity cycle: appear -> stay -> disappear
            // Use sine wave for smooth transition, clamped to 0-1
            // Period: 8 seconds (fade in 2s, visible 2s, fade out 2s, hidden 2s)
            const cycle = t % 10;
            let opacity = 0;

            if (cycle < 2) opacity = cycle / 2; // Fade in
            else if (cycle < 6) opacity = 1;    // Visible
            else if (cycle < 8) opacity = 1 - (cycle - 6) / 2; // Fade out
            else opacity = 0; // Hidden

            const material = ref.current.material as THREE.Material;
            material.transparent = true;
            material.opacity = opacity;

            // Subtle floating movement
            ref.current.position.y = position[1] + Math.sin(t * 0.5) * 0.2;
            ref.current.scale.set(3, 2, 1);
        }
    });

    return (
        <Float speed={2} rotationIntensity={0.2} floatIntensity={0.5}>
            <Image
                ref={ref}
                url={img}
                position={position}
            // scale={2}
            />
        </Float>
    );
};

const ProductShowcase = () => {
    // Fixed positions for products to appear so they don't overlap too much
    const positions: [number, number, number][] = [
        [-5, 2, -2],
        [5, 1, -3],
        [-3, 0, 0],
        [3, 3, -4],
        [0, 1, -1],
        [-4, -1, -5]
    ];

    return (
        <group>
            {productImages.map((img, index) => (
                <FloatingProduct
                    key={index}
                    img={img}
                    position={positions[index % positions.length]}
                    delay={index * 2}
                />
            ))}
        </group>
    );
};

const TechGeometry = ({ position, rotationSpeed = 0.5, color = "#00aaff" }: { position: [number, number, number], rotationSpeed?: number, color?: string }) => {
    const meshRef = useRef<THREE.Mesh>(null);
    useFrame((state, delta) => {
        if (meshRef.current) {
            meshRef.current.rotation.x += delta * rotationSpeed;
            meshRef.current.rotation.y += delta * rotationSpeed * 0.5;
        }
    });

    return (
        <Float speed={2} rotationIntensity={1} floatIntensity={1}>
            <mesh ref={meshRef} position={position}>
                <icosahedronGeometry args={[0.5, 0]} />
                <meshStandardMaterial color={color} wireframe emissive={color} emissiveIntensity={0.5} />
            </mesh>
        </Float>
    );
};

const MovingGrid = () => {
    const gridRef = useRef<THREE.GridHelper>(null);
    useFrame((state) => {
        if (gridRef.current) {
            // Move grid to simulate forward movement
            gridRef.current.position.z = (state.clock.getElapsedTime() * 0.5) % 2;
        }
    });
    return (
        <group position={[0, -2, 0]}>
            <gridHelper ref={gridRef} args={[40, 40, 0x00aaff, 0x112233]} />
        </group>
    );
};

const ThreeBackground = () => {
    return (
        <div style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', zIndex: 0 }}>
            <Canvas>
                <PerspectiveCamera makeDefault position={[0, 2, 8]} fov={60} />
                <color attach="background" args={['#050a14']} />

                {/* Lights */}
                <ambientLight intensity={0.5} />
                <pointLight position={[10, 10, 10]} color="#44aaff" intensity={2} />
                <pointLight position={[-10, 5, -10]} color="#ff00aa" intensity={1} />

                {/* Environment */}
                <Stars radius={100} depth={50} count={7000} factor={4} saturation={0} fade speed={1} />
                <MovingGrid />
                <ProductShowcase />


                {/* Particles */}
                <Sparkles count={500} scale={20} size={3} speed={0.4} opacity={0.5} color="#44aaff" />

                {/* Floating Tech Objects */}
                <TechGeometry position={[-4, 1, -2]} rotationSpeed={0.2} />
                <TechGeometry position={[4, -1, -3]} rotationSpeed={0.3} color="#00ccff" />
                <TechGeometry position={[-2, -1, 2]} rotationSpeed={0.15} color="#3366ff" />
                <TechGeometry position={[3, 3, -1]} rotationSpeed={0.25} />
                <TechGeometry position={[0, 2, -5]} rotationSpeed={0.1} color="#abcdef" />

                {/* Dynamic Movement */}
                <OrbitControls
                    enableZoom={false}
                    enablePan={false}
                    enableRotate={true}
                    autoRotate
                    autoRotateSpeed={0.5}
                    maxPolarAngle={Math.PI / 2 - 0.1} // Prevent going below grid
                    minPolarAngle={Math.PI / 3}
                />

                {/* Fog to blend into background */}
                <fog attach="fog" args={['#050a14', 5, 25]} />
            </Canvas>
        </div>
    );
};

export default ThreeBackground;
